import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Film, Mail } from 'lucide-react';
import { api } from '@/lib/api';
import { useSession } from '@/lib/session';
import { errorMessage } from '@/lib/toast';
import { Button, Input } from '@/components/ui';
import { DesktopSignIn } from '@/components/DesktopApp';

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

export function LoginPage() {
  const [params] = useSearchParams();
  const status = useSession((s) => s.status);
  const load = useSession((s) => s.load);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(params.get('error') ?? '');
  const [providers, setProviders] = useState<{ google: boolean; allowSignups: boolean } | null>(null);
  const redirect = params.get('redirect') ?? '/';

  useEffect(() => {
    void load();
    api<{ google: boolean; allowSignups: boolean }>('/auth/providers').then(setProviders).catch(() => setProviders({ google: false, allowSignups: true }));
  }, [load]);

  if (status === 'signed-in') return <Navigate to={redirect} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/auth/magic', { method: 'POST', json: { email, redirect } });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-[radial-gradient(ellipse_at_top,rgba(124,92,255,0.18),transparent_60%)] p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent shadow-lg shadow-accent/30">
            <Film size={24} className="text-white" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Sign in to vidmakr</h1>
          <p className="text-sm text-muted">
            Collaborative video editing for your team ·{' '}
            <Link to="/welcome" className="text-accent-2 hover:underline">
              Learn more
            </Link>
          </p>
        </div>
        <div className="rounded-2xl border border-line bg-panel p-6 shadow-xl">
          {sent ? (
            <div className="py-4 text-center">
              <Mail className="mx-auto mb-3 text-accent-2" size={28} />
              <div className="font-medium">Check your inbox</div>
              <p className="mt-1 text-sm text-muted">
                We sent a sign-in link to <b className="text-fg">{email}</b>. It expires in 15 minutes.
              </p>
              <Button variant="ghost" className="mt-4" onClick={() => setSent(false)}>
                Use a different email
              </Button>
            </div>
          ) : (
            <>
              {providers?.google ? (
                <>
                  <a href={`/auth/google?redirect=${encodeURIComponent(redirect)}`}>
                    <Button className="w-full" size="md">
                      <GoogleIcon /> Continue with Google
                    </Button>
                  </a>
                  <div className="my-5 flex items-center gap-3 text-xs text-faint">
                    <div className="h-px flex-1 bg-line" /> or <div className="h-px flex-1 bg-line" />
                  </div>
                </>
              ) : null}
              <form onSubmit={submit} className="space-y-3">
                <Input type="email" required placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
                <Button variant="primary" className="w-full" loading={busy} type="submit">
                  Email me a sign-in link
                </Button>
              </form>
              {providers && !providers.allowSignups ? (
                <p className="mt-3 text-center text-xs text-faint">New accounts need an invitation.</p>
              ) : null}
            </>
          )}
          {error ? <div className="mt-4 rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">{error}</div> : null}
          <DesktopSignIn redirect={redirect} />
        </div>
      </div>
    </div>
  );
}
